import { CircularProgress } from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import Mark from 'mark.js';
import React, { ChangeEvent, FocusEvent, RefObject, useRef } from 'react';
import { useState } from 'react';
import { Container, Input, SearchLabel, ClearButton } from './styles';

type Props = {
  onSearch: (term: string) => void;
  isSearching: boolean;
  wasNotFound: boolean;
  amountOfResultsFound: number;
};

export default function SearchInput({ onSearch, isSearching, wasNotFound, amountOfResultsFound }: Props) {
  const [value, setValue] = useState('');

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    setValue(event.target.value);
    onSearch(event.target.value);
  }

  function handleClear() {
    setValue('');
    onSearch('');
  }

  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    setTimeout(() => event.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 500);
  }

  function label() {
    if (wasNotFound) return `Não encontramos nada com '${value}'`;
    if (isSearching) return 'Pesquisando...';
    return `${amountOfResultsFound} marcado${amountOfResultsFound > 1 ? 's' : ''}`;
  }

  const shouldDisplayLabel = Boolean(isSearching || wasNotFound || amountOfResultsFound > 0);

  return (
    <Container>
      <Input value={value} onChange={handleOnChange} onFocus={handleFocus} placeholder="Pesquisar e marcar" />
      {Boolean(value) && (
        <ClearButton onClick={handleClear}>
          <Close />
        </ClearButton>
      )}
      {shouldDisplayLabel && (
        <SearchLabel>
          {isSearching && <CircularProgress size={12} style={{ color: '#999', marginRight: 5 }} />}
          {label()}
        </SearchLabel>
      )}
    </Container>
  );
}

export function useSearchComplementsHandler(
  containerRef: RefObject<HTMLDivElement>,
  complementGroupsBoxRef: RefObject<HTMLDivElement>,
  isTabletOrMobile: boolean
) {
  const [isSearching, setIsSearching] = useState(false);
  const [wasNotFound, setWasNotFound] = useState(false);
  const [amountOfResultsFound, setAmountOfResultsFound] = useState(0);
  const searchTimeout = useRef(0);

  function handler(searchTerm: string) {
    if (!complementGroupsBoxRef.current) return;

    const mark = new Mark(complementGroupsBoxRef.current);
    mark.unmark();
    window.clearTimeout(searchTimeout.current);

    setWasNotFound(false);
    setAmountOfResultsFound(0);
    if (!searchTerm) {
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    searchTimeout.current = window.setTimeout(() => {
      const terms = normalizeSearchTerm();

      mark.mark(terms);
      const markedElements = complementGroupsBoxRef.current?.querySelectorAll('[data-markjs=true]');

      if (!markedElements || markedElements.length === 0) setWasNotFound(true);
      else setAmountOfResultsFound(markedElements.length);

      setIsSearching(false);
    }, 1000);

    function normalizeSearchTerm() {
      const result = searchTerm.replaceAll(' e ', ' ').replaceAll('.', ' ').replaceAll(',', ' ');
      return result
        .split(' ')
        .map((s) => s.trim())
        .filter((s) => !!s);
    }
  }

  return { handler, isSearching, wasNotFound, amountOfResultsFound };
}
